import { AuthenticationService } from "../authenticationService";
import { APIService } from "../apiService";
import {
  PaginatedRelabelCases,
  RelabelCasePaginationOptions,
  RelabelCaseRecord,
  RelabelCaseStatus,
  S3SignedUrl
} from "src/types";
import { PostRelabelCasesRequestBody } from "src/types/api/relabelCases/postRelabelCaseRecord";
import { GetRelabelCaseStatsResponse } from "src/types/api/relabelCases/getRelabelCaseStats";
import { PatchRelabelCaseQuantityRequestBody } from "src/types/api/relabelCases/patchRelabelCaseQuantity";
import { PatchRelabelCaseSimpleEditRequestBody } from "src/types/api/relabelCases/patchRelabelCaseSimpleEdit";
import { PostRelabelCaseRouteResponse } from "src/types/api/relabelCases/postRelabelCaseRoute";
import {
  PostRelabelCaseConfirmPlacementRequestBody,
  PostRelabelCaseConfirmPlacementResponse
} from "src/types/api/relabelCases/postRelabelCaseConfirmPlacement";

class RelabelCaseAPIService extends APIService {
  constructor(authenticationService: AuthenticationService) {
    super(authenticationService, "/v1/relabel-cases");
  }

  async postRelabelCases(requestBody: PostRelabelCasesRequestBody): Promise<RelabelCaseRecord[]> {
    return await this.jwtRequest("POST", "", requestBody);
  }

  async getRelabelCasesByPurchaseOrderId(purchaseOrderId: string): Promise<RelabelCaseRecord[]> {
    return await this.jwtRequest("GET", "/by-purchase-order", undefined, { purchaseOrderId });
  }

  async getRelabelCasesByTransferOrderId(transferOrderId: string): Promise<RelabelCaseRecord[]> {
    return await this.jwtRequest("GET", "/by-transfer-order", undefined, { transferOrderId });
  }

  async getRelabelCases(queryParams?: RelabelCasePaginationOptions): Promise<PaginatedRelabelCases> {
    return await this.jwtRequest("GET", "", undefined, queryParams);
  }

  async patchRelabelCaseStatus(caseId: string, status: RelabelCaseStatus): Promise<RelabelCaseRecord> {
    return await this.jwtRequest("PATCH", `/${encodeURIComponent(caseId)}/status`, { status });
  }

  async patchRelabelCaseQuantity(
    caseId: string,
    body: PatchRelabelCaseQuantityRequestBody
  ): Promise<RelabelCaseRecord> {
    return await this.jwtRequest("PATCH", `/${encodeURIComponent(caseId)}/quantity`, body);
  }

  async patchRelabelCaseSimpleEdit(
    caseId: string,
    body: PatchRelabelCaseSimpleEditRequestBody
  ): Promise<RelabelCaseRecord> {
    return await this.jwtRequest("PATCH", `/${encodeURIComponent(caseId)}/simple-edit`, body);
  }

  async getRelabelCaseLabelSignedUrl(caseId: string): Promise<S3SignedUrl> {
    return await this.jwtRequest("GET", `/${encodeURIComponent(caseId)}/label-url`);
  }

  async getRelabelCaseStats(): Promise<GetRelabelCaseStatsResponse> {
    return await this.jwtRequest("GET", "/stats");
  }

  async getCompleteCases(queryParams?: RelabelCasePaginationOptions): Promise<PaginatedRelabelCases> {
    return await this.jwtRequest("GET", "/complete", undefined, queryParams);
  }

  async postRelabelCaseRoute(caseId: string): Promise<PostRelabelCaseRouteResponse> {
    return await this.jwtRequest("POST", `/${encodeURIComponent(caseId)}/route`, {});
  }

  async deleteRelabelCaseRoute(caseId: string): Promise<RelabelCaseRecord> {
    return await this.jwtRequest("DELETE", `/${encodeURIComponent(caseId)}/route`);
  }

  async postRelabelCaseConfirmPlacement(
    caseId: string,
    body: PostRelabelCaseConfirmPlacementRequestBody
  ): Promise<PostRelabelCaseConfirmPlacementResponse> {
    return await this.jwtRequest("POST", `/${encodeURIComponent(caseId)}/confirm-placement`, body);
  }

  async postRelabelCasePrintEvent(caseId: string, body: { printerName: string; printerUid?: string }): Promise<void> {
    return await this.jwtRequest("POST", `/${encodeURIComponent(caseId)}/print-event`, body);
  }
}

export { RelabelCaseAPIService };
